<template>
  <hb-basic-page :back-button="backButton" :extension-height="56" :fill-height="loading" :loading="loading" :title="title" :no-gutters="true" no-container-padding>
    <template v-slot:app-bar-extension>
      <v-col class="px-0">
        <v-row align="center" class="flex-nowrap">
          <v-select
            v-if="quotation"
            :value="currentTabItem"
            :items="tabItems"
            :menu-props="{ maxHeight: 'none', contentClass: 'tab-select-menu' }"
            item-text="name"
            return-object
            dense
            solo
            hide-details
            background-color="grey darken-2"
            dark
            class="tab-select ml-2"
            @change="onTabSelect"
          >
            <template v-slot:prepend-inner>
              <v-icon small class="mr-1">mdi-menu</v-icon>
            </template>
          </v-select>

          <v-spacer />

          <div v-if="quotation" class="d-flex align-center mr-2">
            <template v-if="$vuetify.breakpoint.smAndUp">
              <quotation-status-chip v-if="id" :status="quotation.status" class="mr-3" />
              <template v-if="!isRequest">
                <span class="white--text subtitle-1 font-weight-bold text-no-wrap">{{ $formatNumber(total, '0,0') }} €</span>
                <span class="white--text caption ml-1 mr-4 text-no-wrap">alv 0 %</span>
              </template>
              <v-btn v-if="canSave" :disabled="!isDirty || saving" :loading="saving" color="primary" @click="save">
                <v-icon left>mdi-content-save</v-icon>
                {{ $t('save') }}
              </v-btn>
            </template>
            <v-menu v-if="id" offset-y left>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon dark v-bind="attrs" v-on="on" :title="$t('quotations.actions.menu')" class="ml-1">
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item @click="openCopyDialog">
                  <v-list-item-icon><v-icon>mdi-content-copy</v-icon></v-list-item-icon>
                  <v-list-item-title>{{ $t('quotations.actions.copy') }}</v-list-item-title>
                </v-list-item>
                <v-list-item @click="openMoveDialog(false)">
                  <v-list-item-icon><v-icon>mdi-folder-move-outline</v-icon></v-list-item-icon>
                  <v-list-item-title>{{ $t('quotations.actions.move') }}</v-list-item-title>
                </v-list-item>
                <v-list-item v-if="quotation.siblings.length > 0" @click="openMoveDialog(true)">
                  <v-list-item-icon><v-icon>mdi-folder-remove-outline</v-icon></v-list-item-icon>
                  <v-list-item-title>{{ $t('quotations.actions.detach') }}</v-list-item-title>
                </v-list-item>
                <v-list-item @click="openReviewDialog">
                  <v-list-item-icon><v-icon>mdi-account-check-outline</v-icon></v-list-item-icon>
                  <v-list-item-title>{{ $t('quotations.actions.request_review') }}</v-list-item-title>
                </v-list-item>
                <v-list-item v-if="isRequest || quotation.status === 'CALCULATION'" @click="deleteDraft">
                  <v-list-item-icon><v-icon color="error">delete</v-icon></v-list-item-icon>
                  <v-list-item-title>{{ $t('quotations.actions.delete_draft') }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </v-row>
      </v-col>
    </template>

    <v-col v-if="quotation" cols="12" class="pb-16">
      <v-container fluid :class="$vuetify.breakpoint.xsOnly && 'pa-0'">
        <v-row :no-gutters="$vuetify.breakpoint.xsOnly">
          <v-col :class="$vuetify.breakpoint.xsOnly ? 'gradient-icons pa-0 ma-0' : 'gradient-icons mt-3'" lg="8" md="8" offset-lg="2" offset-md="2">
            <div :class="$vuetify.breakpoint.xsOnly ? 'px-4 pt-3' : ''">
              <v-alert v-if="quotation.acceptedProject" type="success" color="green darken-4" text dense class="mb-3">
                <v-icon color="green darken-4" small class="no-gradient mr-1">mdi-office-building</v-icon>
                <router-link :to="{ name: 'tenant_project_edit_project', params: { id: quotation.acceptedProject.id } }" class="green--text text--darken-4">
                  {{ $t('quotations.accepted_project', { code: quotation.acceptedProject.code }) }} · {{ quotation.acceptedProject.name }}
                </router-link>
                <span v-if="quotation.acceptedProject.ordererName"> · {{ quotation.acceptedProject.ordererName }}</span>
              </v-alert>
              <v-alert v-if="editNotice" type="warning" text dense class="mb-3">{{ editNotice }}</v-alert>
            </div>

            <quotation-project-header :quotation="quotation" :saving="saving" />

            <quotation-status-stepper
              v-if="id"
              :quotation="quotation"
              :blockers="blockers"
              :warnings="warnings"
              :disabled="saving"
              :class="$vuetify.breakpoint.xsOnly ? 'mx-4' : ''"
              @change-status="changeStatus"
            />

            <v-tabs-items :value="activeTab" touchless>
              <v-tab-item value="basic">
                <template v-if="activeTab === 'basic'">
                  <quotation-request-card
                    :quotation="quotation"
                    :parties="parties"
                    :persons="persons"
                    :saving="saving"
                    :persisted="!!id"
                    @recipient-updated="onRecipientUpdated"
                    @accept="changeStatus('ORDERED')"
                  />
                  <quotation-basics-tab :quotation="quotation" :users="users" :saving="saving" />
                </template>
              </v-tab-item>
              <v-tab-item value="calculation">
                <quotation-calculation-tab
                  v-if="activeTab === 'calculation'"
                  :quotation="quotation"
                  :work-methods="workMethods"
                  :items="items"
                  :readonly="$vuetify.breakpoint.xsOnly"
                  :saving="saving"
                />
              </v-tab-item>
              <v-tab-item value="texts">
                <quotation-texts-tab v-if="activeTab === 'texts'" :quotation="quotation" :saving="saving" @copy-texts="openCopyTextsDialog" />
              </v-tab-item>
              <v-tab-item value="notes">
                <quotation-notes-tab v-if="activeTab === 'notes'" :quotation="quotation" :total="total" :saving="saving" />
              </v-tab-item>
              <v-tab-item value="attachments">
                <quotation-attachments-tab v-if="activeTab === 'attachments'" :quotation="quotation" />
              </v-tab-item>
            </v-tabs-items>
          </v-col>
        </v-row>
      </v-container>
    </v-col>

    <v-btn
      v-if="quotation && $vuetify.breakpoint.xsOnly"
      :disabled="!isDirty || saving"
      :loading="saving"
      :title="$t('save')"
      bottom
      color="primary"
      fab
      fixed
      right
      @click="save"
    >
      <v-icon>mdi-content-save</v-icon>
    </v-btn>

    <status-reason-dialog ref="reasonDialog" />
    <accept-quotation-dialog ref="acceptDialog" :parties="parties" :persons="persons" @accepted="onAccepted" />
    <copy-quotation-dialog ref="copyDialog" :parties="parties" :persons="persons" @copied="onCopied" @texts="applyCopiedTexts" />
    <move-quotation-dialog ref="moveDialog" @moved="onMoved" />
    <review-request-dialog ref="reviewDialog" @sent="onReviewSent" />
  </hb-basic-page>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import quotationApi, { blockers as computeBlockers, warnings as computeWarnings, REASON_REQUIRED, emptyRecipient } from '@/api/quotation';
import { quotationTotal } from '@/helpers/quotationCalc';
import QuotationStatusChip from '@/components/Quotations/QuotationStatusChip';
import QuotationStatusStepper from '@/components/Quotations/QuotationStatusStepper';
import StatusReasonDialog from '@/components/Quotations/StatusReasonDialog';
import QuotationBasicsTab from '@/components/Quotations/QuotationBasicsTab';
import QuotationCalculationTab from '@/components/Quotations/QuotationCalculationTab';
import QuotationTextsTab from '@/components/Quotations/QuotationTextsTab';
import QuotationRequestCard from '@/components/Quotations/QuotationRequestCard';
import QuotationProjectHeader from '@/components/Quotations/QuotationProjectHeader';
import QuotationNotesTab from '@/components/Quotations/QuotationNotesTab';
import QuotationAttachmentsTab from '@/components/Quotations/QuotationAttachmentsTab';
import AcceptQuotationDialog from '@/components/Quotations/AcceptQuotationDialog';
import CopyQuotationDialog from '@/components/Quotations/CopyQuotationDialog';
import MoveQuotationDialog from '@/components/Quotations/MoveQuotationDialog';
import ReviewRequestDialog from '@/components/Quotations/ReviewRequestDialog';

const TABS = ['basic', 'calculation', 'texts', 'notes', 'attachments'];
// Tarjouspyynnöstä kirjataan vain pyyntö ja aineisto; laskenta, tekstit ja muistio tulevat, kun siirretään laskentaan
const REQUEST_TABS = ['basic', 'attachments'];

function emptyQuotation() {
  return {
    name: '',
    offerProjectId: null,
    siblings: [],
    status: 'REQUEST',
    version: 1,
    authorUserId: null,
    request: { description: '', desiredStart: null, plannedEnd: null },
    sites: [],
    texts: { intro: '', included: '', reservations: '', obligations: '', other: '', contact: '' },
    paymentTerm: '14',
    paymentTermText: '',
    duration: '',
    recipient: emptyRecipient(),
    calculationNotes: '',
    costEstimate: null,
  };
}

export default {
  name: 'QuotationView',
  components: {
    QuotationStatusChip,
    QuotationStatusStepper,
    StatusReasonDialog,
    QuotationBasicsTab,
    QuotationCalculationTab,
    QuotationTextsTab,
    QuotationRequestCard,
    QuotationProjectHeader,
    QuotationNotesTab,
    QuotationAttachmentsTab,
    AcceptQuotationDialog,
    CopyQuotationDialog,
    MoveQuotationDialog,
    ReviewRequestDialog,
  },
  props: {
    id: { type: [Number, String], default: null },
    tab: { type: String, default: 'basic' },
  },
  data() {
    return {
      loading: true,
      saving: false,
      quotation: null,
      quotationWhenLoaded: null,
      selectedTab: 'basic',
      users: [],
      workMethods: [],
      items: [],
      parties: [],
      persons: [],
      leaving: false,
    };
  },
  computed: {
    title() {
      if (!this.quotation) return '';
      if (!this.id) return this.$t('quotations.new');
      return this.quotation.name || '';
    },
    total() {
      return quotationTotal(this.quotation);
    },
    isDirty() {
      return !isEqual(this.quotationWhenLoaded, this.quotation);
    },
    canSave() {
      return !!this.quotation;
    },
    isRequest() {
      return !!this.quotation && this.quotation.status === 'REQUEST';
    },
    // Sisältöä ei lukita laskennan jälkeen; muokkauksesta huomautetaan, ja vastuu on muokkaajalla
    editNotice() {
      if (!this.id || !this.isDirty || this.isRequest || this.quotation.status === 'CALCULATION') return null;
      return this.$t('quotations.edit_notice', { status: this.$t(`quotations.statuses.${this.quotation.status}`) });
    },
    blockers() {
      return this.quotation && this.quotation.status === 'CALCULATION' ? computeBlockers(this.quotation) : [];
    },
    warnings() {
      return this.quotation && this.quotation.status === 'CALCULATION' ? computeWarnings(this.quotation) : [];
    },
    tabItems() {
      // Liitteet tarvitsevat tallennetun tarjouksen; muistio tallentuu tarjouksen mukana jo luotaessa
      const tabs = (this.isRequest ? REQUEST_TABS : TABS).filter(t => this.id || t !== 'attachments');
      return tabs.map(t => ({ tab: t, name: this.$t(`quotations.tabs.${t}`) }));
    },
    currentTabItem() {
      return this.tabItems.find(t => t.tab === this.selectedTab) || this.tabItems[0];
    },
    // URL:n välilehti, jota vaihe ei näytä (esim. laskenta tarjouspyynnöllä), näytetään perustietoina
    activeTab() {
      return this.currentTabItem.tab;
    },
    backButton() {
      // Välilehdet vaihdetaan router.replace-kutsulla, joten historian edellinen sivu on aina tarjouslista
      return { show: true, fallback: 'tenant_quotations' };
    },
  },
  watch: {
    tab: {
      immediate: true,
      handler(v) {
        if (this.id) this.selectedTab = TABS.includes(v) ? v : 'basic';
      },
    },
    id() {
      this.load();
    },
  },
  // Tallentamattomat muutokset eivät saa kadota takaisin-nuolesta tai valikosta huomaamatta
  async beforeRouteLeave(to, from, next) {
    if (this.isDirty && !this.leaving && to.name !== 'tenant_quotation_edit') {
      next(await this.saveBeforeLeaving());
      return;
    }
    next();
  },
  // Saman projektin toiseen tarjoukseen siirtyminen käyttää samaa näkymää; välilehden vaihto ei ole poistuminen
  async beforeRouteUpdate(to, from, next) {
    if (this.isDirty && !this.leaving && String(to.params.id) !== String(from.params.id)) {
      next(await this.saveBeforeLeaving());
      return;
    }
    next();
  },
  async mounted() {
    await this.loadLookups();
    await this.load();
  },
  methods: {
    async loadLookups() {
      try {
        [this.users, this.workMethods, this.items, this.parties, this.persons] = await Promise.all([
          quotationApi.getUsers(),
          quotationApi.getWorkMethods(),
          quotationApi.getItems(),
          quotationApi.getParties(),
          quotationApi.getPersons(),
        ]);
      } catch (error) {
        this.$handleApiError(error);
      }
    },
    async load() {
      this.loading = true;
      try {
        if (this.id) {
          this.quotation = await quotationApi.getQuotation(this.id);
        } else {
          this.quotation = emptyQuotation();
          this.selectedTab = 'basic';
        }
        this.quotationWhenLoaded = cloneDeep(this.quotation);
      } catch (error) {
        this.$handleApiError(error);
      }
      this.loading = false;
    },
    onTabSelect(item) {
      if (this.id) {
        this.$router.replace({ name: 'tenant_quotation_edit', params: { id: this.id, tab: item.tab } });
      } else {
        this.selectedTab = item.tab;
      }
    },
    async saveBeforeLeaving() {
      const ok = await this.$confirm('warning', this.$t('save'), this.$t('quotations.unsaved_changes'));
      return ok ? this.save() : false;
    },
    // Tallentamattomat muutokset tallennetaan ennen toimintoa, joka hakee tarjouksen palvelimelta uudelleen
    async saveFirst() {
      if (!this.isDirty) return true;
      const ok = await this.$confirm('primary', this.$t('save'), this.$t('quotations.unsaved_changes'));
      return ok && this.save();
    },
    async save() {
      if (!this.quotation.name || !this.quotation.name.trim()) {
        this.$showErrorNotification(this.$t('quotations.blockers.name_missing'));
        return false;
      }
      if (!this.quotation.recipient.partyId || !this.quotation.recipient.deadline) {
        this.$showErrorNotification(this.$t('quotations.recipients.party_and_deadline_required'));
        return false;
      }
      this.saving = true;
      try {
        if (this.id) {
          this.quotation = await quotationApi.updateQuotation(this.id, this.quotation);
          this.quotationWhenLoaded = cloneDeep(this.quotation);
          this.$showSuccessNotification(this.$t('quotations.notifications.saved'));
        } else {
          const created = await quotationApi.createQuotation(this.quotation);
          this.$showSuccessNotification(this.$t('quotations.notifications.created'));
          this.quotationWhenLoaded = cloneDeep(this.quotation);
          this.$router.replace({ name: 'tenant_quotation_edit', params: { id: created.id, tab: this.selectedTab } });
        }
        this.saving = false;
        return true;
      } catch (error) {
        this.$handleApiError(error, this.$t('quotations.notifications.error'));
        this.saving = false;
        return false;
      }
    },
    async changeStatus(status) {
      if (!(await this.saveFirst())) return;
      if (status === 'ORDERED') {
        this.$refs.acceptDialog.open(this.quotation);
        return;
      }
      const from = this.quotation.status;
      let reason = null;
      // Tarjouspyynnön "Ei tarjota" on lopullinen, mutta syytä ei vaadita
      if (from === 'REQUEST' && status === 'DECLINED') {
        const ok = await this.$confirm('primary', this.$t('quotations.actions.decline'), this.$t('quotations.decline_request_confirm', { name: this.quotation.name }));
        if (!ok) return;
      }
      if (REASON_REQUIRED[`${from}>${status}`]) {
        const kind = status === 'NOT_ORDERED' ? 'reject' : status === 'DECLINED' ? 'cancel' : 'return';
        reason = await this.$refs.reasonDialog.open(kind);
        if (!reason) return;
      }
      this.saving = true;
      try {
        this.quotation = await quotationApi.changeStatus(this.id, status, reason);
        this.quotationWhenLoaded = cloneDeep(this.quotation);
        this.$showSuccessNotification(this.$t('quotations.notifications.status_changed'));
      } catch (error) {
        this.$handleApiError(error, error && error.message);
      }
      this.saving = false;
    },
    async deleteDraft() {
      const ok = await this.$confirmDelete(this.$t('quotations.delete_draft_confirm', { name: this.quotation.name }), this.$t('quotations.actions.delete_draft'));
      if (!ok) return;
      try {
        await quotationApi.deleteQuotation(this.id);
        this.leaving = true;
        this.$showSuccessNotification(this.$t('quotations.notifications.deleted'));
        this.$router.push({ name: 'tenant_quotations' });
      } catch (error) {
        this.$handleApiError(error, error && error.message);
      }
    },
    onRecipientUpdated(updated) {
      // Vain palvelimen muuttamat kentät, jotta muiden välilehtien tallentamattomat muokkaukset säilyvät
      ['recipient', 'status', 'updatedAt'].forEach(k => {
        this.$set(this.quotation, k, updated[k]);
        this.$set(this.quotationWhenLoaded, k, cloneDeep(updated[k]));
      });
    },
    onAccepted(updated) {
      this.quotation = updated;
      this.quotationWhenLoaded = cloneDeep(updated);
      this.$showSuccessNotification(this.$t('quotations.accept.created', { code: updated.acceptedProject.code, n: updated.acceptedProject.siteCount }));
      this.$router.push({ name: 'tenant_project_edit_project', params: { id: updated.acceptedProject.id } });
    },
    async openCopyDialog() {
      if (!(await this.saveFirst())) return;
      const q = this.quotation;
      this.$refs.copyDialog.open({ id: q.id, name: q.name, code: q.recipient.code, deadline: q.recipient.deadline });
    },
    async openMoveDialog(detach) {
      if (!(await this.saveFirst())) return;
      this.$refs.moveDialog.open(this.quotation, { detach });
    },
    onMoved(updated) {
      this.quotation = updated;
      this.quotationWhenLoaded = cloneDeep(updated);
      this.$showSuccessNotification(this.$t('quotations.move.moved', { name: updated.name }));
    },
    // Tarkistaja näkee tallennetun version, joten muutokset tallennetaan ensin
    async openReviewDialog() {
      if (!(await this.saveFirst())) return;
      this.$refs.reviewDialog.open(this.quotation);
    },
    onReviewSent(user) {
      this.$showSuccessNotification(this.$t('quotations.review_request.sent', { name: `${user.firstName} ${user.lastName}`.trim() }));
    },
    openCopyTextsDialog() {
      this.$refs.copyDialog.openTexts(this.quotation);
    },
    onCopied(created) {
      this.$showSuccessNotification(this.$t('quotations.notifications.copied'));
      this.leaving = true;
      this.$router.push({ name: 'tenant_quotation_edit', params: { id: created.id, tab: 'basic' } });
    },
    applyCopiedTexts({ texts, name }) {
      Object.keys(texts).forEach(k => this.$set(this.quotation.texts, k, texts[k]));
      this.$showSuccessNotification(this.$t('quotations.copy.texts_copied', { name }));
    },
  },
};
</script>

<style lang="scss" scoped>
.tab-select {
  flex: 1;
  max-width: 300px;
  font-size: 1.1rem;
}

.v-tabs-items {
  background-color: transparent !important;
}
</style>

<style lang="scss">
.tab-select-menu {
  .v-list-item {
    font-size: 1.1rem;
    .v-list-item__title {
      font-size: 1.1rem;
    }
  }
}
</style>
